import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { userProfileSyncService } from '@/shared/services/userProfileSyncService';
import { UserRole, UserProfile } from '@/shared/types';
import { isAdmin, hasRole, hasAnyRole } from '@/features/auth/services/roleService';
import { authAuditLogger } from '@/shared/services/auth/authAuditLogger';
import FullPageSkeleton from '@/shared/components/loading/FullPageSkeleton';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required role(s) to access this route */
  requiredRoles?: UserRole | UserRole[];
  /** Where to redirect if unauthorized (default: /client/dashboard) */
  redirectTo?: string;
  /** Show unauthorized page instead of redirecting */
  showUnauthorizedPage?: boolean;
}

/**
 * Auth-protected route wrapper with role-based access control
 * Ensures user is authenticated and has the required role before accessing protected routes
 *
 * This component handles the "loading state" properly to avoid redirect loops on refresh.
 * The key insight: always show a loading state while auth is being determined,
 * and only redirect once we have a definitive answer (authenticated or not).
 *
 * USAGE:
 * ```tsx
 * // Require any authenticated user
 * <ProtectedRoute><Dashboard /></ProtectedRoute>
 *
 * // Require admin role
 * <ProtectedRoute requiredRoles={UserRole.ADMIN}><AdminPanel /></ProtectedRoute>
 *
 * // Require admin or freelancer
 * <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FREELANCER]}>
 *   <ManagementPanel />
 * </ProtectedRoute>
 * ```
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  redirectTo = '/client/dashboard',
  showUnauthorizedPage = false
}) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let isSubscribed = true;
    let authCheckTimeout: NodeJS.Timeout | null = null;

    // Use the listener approach - the firebaseAuthService.onAuthStateChanged()
    // immediately calls the callback with current auth state if already known
    const unsubscribe = firebaseAuthService.onAuthStateChanged(async (firebaseUser) => {
      if (!isSubscribed) {
        return;
      }

      if (firebaseUser) {
        try {
          // Get user profile to determine role
          const profile = await userProfileSyncService.getOrCreateSupabaseProfile(firebaseUser);
          if (isSubscribed) {
            setUser(profile);
            setIsAuthChecked(true);
            setHasCheckedAuth(true);
          }
        } catch (profileError) {
          console.warn('Could not fetch user profile:', profileError);
          if (isSubscribed) {
            setUser(null);
            setIsAuthChecked(true);
            setHasCheckedAuth(true);
          }
        }
      } else {
        // Clear any pending timeout when we get a null callback
        if (authCheckTimeout) {
          clearTimeout(authCheckTimeout);
          authCheckTimeout = null;
        }

        // Debounce setting user to null to avoid transient null states during navigation
        // Only set to null if Firebase user is still null after a short delay
        authCheckTimeout = setTimeout(() => {
          if (isSubscribed) {
            // Double-check Firebase auth state before setting user to null
            const auth = firebaseAuthService.getAuth();
            if (!auth?.currentUser) {
              setUser(null);
            }
            setIsAuthChecked(true);
            setHasCheckedAuth(true);
          }
        }, 100); // 100ms debounce to handle transient null states
      }
    });

    return () => {
      isSubscribed = false;
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout);
      }
      unsubscribe();
    };
  }, []); // Keep empty dependency array - only run once on mount

  // Show loading while checking auth state
  if (!hasCheckedAuth || !isAuthChecked) {
    return <FullPageSkeleton />;
  }

  // Check Firebase auth state directly before redirecting
  // This prevents redirecting to login if we have a valid Firebase session
  if (!user) {
    const auth = firebaseAuthService.getAuth();
    const firebaseUser = auth?.currentUser;

    // If Firebase has a user but we don't have a profile, try to get it again
    if (firebaseUser) {
      console.log('🔄 Firebase user exists but profile is null, retrying profile fetch...');
      userProfileSyncService.getOrCreateSupabaseProfile(firebaseUser)
        .then(profile => {
          setUser(profile);
        })
        .catch(err => {
          console.error('Failed to fetch profile on retry:', err);
        });
      return <FullPageSkeleton />;
    }

    // Only redirect if Firebase auth also has no user
    console.log('🚫 No Firebase user, redirecting to auth...');
    sessionStorage.setItem('intendedDestination', location.pathname);
    return <Navigate to="/auth" replace />;
  }

  // Check role requirements if specified
  if (requiredRoles) {
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

    if (!hasAnyRole(user, roles)) {
      // Log unauthorized access attempt
      authAuditLogger.logUnauthorizedAccess({
        userId: user.id,
        email: user.email,
        attemptedPath: location.pathname,
        requiredRole: roles.join(' or '),
        userRole: user.role
      });

      // User doesn't have required role
      // Show unauthorized page or redirect to fallback
      if (showUnauthorizedPage) {
        return <Navigate to="/unauthorized" replace />;
      }

      // Redirect admin users to admin dashboard, others to client dashboard
      const fallbackPath = isAdmin(user) ? '/admin/dashboard' : redirectTo;
      return <Navigate to={fallbackPath} replace />;
    }

    // Log successful role check
    authAuditLogger.logRoleCheck({
      userId: user.id,
      email: user.email,
      requiredRole: roles.join(' or '),
      userRole: user.role,
      outcome: 'allowed'
    });
  }

  return <>{children}</>;
};

export default ProtectedRoute;
